import React from 'react';
import { Spinner } from 'react-bootstrap';
import {
  FaLeaf,
  FaTint,
  FaChartBar,
  FaSave,
  FaArrowLeft,
  FaFileDownload,
  FaEdit,
} from 'react-icons/fa';
import { YButton } from '../../../ui/YButton';
import { PlantingGroupSettingsForm } from './PlantingGroupSettingsForm';
import {
  buildCalculatorBreakdown,
  formatPlotAcreageBadge,
} from './plantingAssistantUtils';

function PresetPickerRow({
  preset,
  isSelected,
  acres,
  accentClass,
  onSelect,
  disabled = false,
}) {
  const rate = Number(preset.default_rate_lbs_per_acre) || 0;
  const bagWeight = Number(preset.bag_weight_lbs) || 50;
  const totalLbs = acres > 0 ? rate * acres : 0;

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(preset.id);
    }
  };

  return (
    <div
      role="radio"
      aria-checked={isSelected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      className={`plant-asst-calc-preset${isSelected ? ` is-selected ${accentClass}` : ''}${disabled ? ' is-disabled' : ''}`}
      onClick={() => {
        if (!disabled) onSelect(preset.id);
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="plant-asst-calc-preset__radio" aria-hidden="true">
        {isSelected ? <span className="plant-asst-calc-preset__radio-dot" /> : null}
      </div>
      <div className="plant-asst-calc-preset__body">
        <p className="plant-asst-calc-preset__name">{preset.label}</p>
        <p className="plant-asst-calc-preset__meta">
          {rate} lbs/acre · {bagWeight} lb bags
        </p>
        {preset.notes ? (
          <p className="plant-asst-calc-preset__note">{preset.notes}</p>
        ) : null}
      </div>
      {acres > 0 && (
        <div className="plant-asst-calc-preset__amount">
          <p className="plant-asst-calc-preset__lbs">{Math.round(totalLbs)} lbs</p>
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ item, kind }) {
  if (!item) return null;
  return (
    <div className="plant-asst-calc-breakdown__row">
      <div>
        <p className="plant-asst-calc-breakdown__label">
          {item.label}
          {item.isPreview ? (
            <span className="plant-asst-calc-breakdown__preview-tag">Preview</span>
          ) : null}
        </p>
        <p className="plant-asst-calc-breakdown__detail">
          {item.totalLbs} lbs total · {item.rateLbsPerAcre} lbs/ac · {item.bagsNeeded} bags (
          {item.bagWeightLbs} lbs each)
        </p>
      </div>
      <p className="plant-asst-calc-breakdown__kind">{kind}</p>
    </div>
  );
}

export function PlantingSeedCalculatorTab({
  selectedPolygons,
  selectedTotal,
  seedOptions,
  fertilizerOptions,
  selectedSeedId,
  selectedFertilizerId,
  onSeedSelect,
  onFertilizerSelect,
  recommendationResult,
  recommendationLoading,
  recommendationError,
  onSaveEstimate,
  onGoToPlots,
  onEditGroup,
  saveEstimateDisabled,
  saveEstimateLoading = false,
  calculatorMode = 'compose',
  canExportPdf = false,
  exportPdfLoading = false,
  onExportPdf,
  draftGroupName,
  onDraftGroupNameChange,
  draftGroupNote,
  onDraftGroupNoteChange,
  editingZoneLabel,
  editingManualOverride,
  onDraftManualToggle,
  draftZoneCode,
  onDraftZoneCodeChange,
  zoneOptionsList,
  formatZoneOptionLabel,
  optionsLoading,
  draftSoilType,
  onDraftSoilTypeChange,
  soilTypeOptions,
  draftSoilDrainage,
  onDraftSoilDrainageChange,
  soilDrainageOptions,
  draftSoilPh,
  onDraftSoilPhChange,
  phHelperText,
  soilPhGuidanceBlurb,
  draftSoilNotes,
  onDraftSoilNotesChange,
  soilNotePlaceholder,
  optionsError,
  detectZoneLoading,
  detectZoneError,
  onDetectZone,
  editingGroupId,
  saveError,
  onCancelEditGroup,
}) {
  const selectedCount = selectedPolygons.length;
  const isViewMode = calculatorMode === 'view';
  const isEditMode = calculatorMode === 'edit';
  const isComposeMode = calculatorMode === 'compose';
  const hasSavedGroup = isViewMode || isEditMode;
  const selectedSeedPreset = seedOptions.find((p) => p.id === selectedSeedId);
  const selectedFertilizerPreset = fertilizerOptions.find((p) => p.id === selectedFertilizerId);

  const breakdown = buildCalculatorBreakdown({
    acres: selectedTotal,
    seedPreset: selectedSeedPreset,
    fertilizerPreset: selectedFertilizerPreset,
    seedId: selectedSeedId,
    fertilizerId: selectedFertilizerId,
    recommendationResult,
  });

  const showBreakdown = selectedCount > 0 && (selectedSeedId || selectedFertilizerId);

  let groupIntro = 'Name this group and set soil details before choosing seed and fertilizer.';
  if (isViewMode) {
    groupIntro = 'Saved group details. Adjust seed or fertilizer below to update recommendations in real time.';
  } else if (isEditMode) {
    groupIntro = `Editing ${draftGroupName || 'this group'}. Update details below or change plot selection on My Plots.`;
  }

  if (selectedCount === 0) {
    return (
      <div className="plant-asst-calc-empty" data-testid="plant-asst-calculator-tab">
        <p className="plant-asst-calc-empty__title">No plots selected</p>
        <p className="plant-asst-calc-empty__text">
          Select one or more plots on My Plots, then use Calculate seed &amp; fertilizer to build an estimate here.
        </p>
        <YButton variant="secondary" size="sm" onClick={onGoToPlots}>
          <FaArrowLeft aria-hidden="true" />
          Back to My Plots
        </YButton>
      </div>
    );
  }

  return (
    <div className="plant-asst-calc" data-testid="plant-asst-calculator-tab">
      <div className="plant-asst-calc-summary" data-testid="plant-asst-calc-summary">
        <div>
          <p className="plant-asst-calc-summary__label">Selected area</p>
          <p className="plant-asst-calc-summary__acres">{formatPlotAcreageBadge(selectedTotal)} acres</p>
          <p className="plant-asst-calc-summary__hint">
            {selectedCount} plot{selectedCount === 1 ? '' : 's'} selected
          </p>
        </div>
        <div className="plant-asst-calc-summary__total">
          <p className="plant-asst-calc-summary__label">Estimated material</p>
          <p className="plant-asst-calc-summary__value">
            {recommendationLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating…
              </>
            ) : breakdown.totalLbs > 0 ? `${Math.round(breakdown.totalLbs)} lbs` : '—'}
          </p>
          <p className="plant-asst-calc-summary__hint">
            {hasSavedGroup ? 'Adjusted rates' : 'Preview — save to apply soil & zone'}
          </p>
        </div>
      </div>

      <section className="plant-asst-calc-group" data-testid="plant-asst-calc-group-section">
        <div className="plant-asst-calc-panel__header">
          <span>Group information</span>
          {isViewMode ? (
            <span className="plant-asst-calc-mode-badge" data-testid="plant-asst-calc-mode-view">Viewing</span>
          ) : null}
          {isEditMode ? (
            <span className="plant-asst-calc-mode-badge plant-asst-calc-mode-badge--edit" data-testid="plant-asst-calc-mode-edit">Editing</span>
          ) : null}
        </div>
        <PlantingGroupSettingsForm
          idPrefix={isComposeMode ? 'compose' : 'calc'}
          variant={hasSavedGroup ? 'edit' : 'create'}
          readOnly={isViewMode}
          intro={groupIntro}
          groupName={draftGroupName}
          onGroupNameChange={onDraftGroupNameChange}
          groupNote={draftGroupNote}
          onGroupNoteChange={onDraftGroupNoteChange}
          zoneLabel={editingZoneLabel}
          manualOverride={editingManualOverride}
          onManualOverrideChange={onDraftManualToggle}
          zoneCode={draftZoneCode}
          onZoneCodeChange={onDraftZoneCodeChange}
          zoneOptionsList={zoneOptionsList}
          formatZoneOptionLabel={formatZoneOptionLabel}
          onDetectZone={onDetectZone}
          detectZoneLoading={detectZoneLoading}
          detectZoneError={detectZoneError}
          showDetectZone={!editingManualOverride && Boolean(editingGroupId) && !isViewMode}
          soilType={draftSoilType}
          onSoilTypeChange={onDraftSoilTypeChange}
          soilTypeOptions={soilTypeOptions}
          soilDrainage={draftSoilDrainage}
          onSoilDrainageChange={onDraftSoilDrainageChange}
          soilDrainageOptions={soilDrainageOptions}
          soilPh={draftSoilPh}
          onSoilPhChange={onDraftSoilPhChange}
          phHelperText={phHelperText}
          soilPhGuidanceBlurb={soilPhGuidanceBlurb}
          soilNotes={draftSoilNotes}
          onSoilNotesChange={onDraftSoilNotesChange}
          soilNotePlaceholder={soilNotePlaceholder}
          optionsLoading={optionsLoading}
          optionsError={optionsError}
          errorMessage={saveError}
          footer={isEditMode ? (
            <YButton variant="ghost" onClick={onCancelEditGroup}>
              Cancel editing
            </YButton>
          ) : null}
        />
      </section>

      <div className="plant-asst-calc-grid">
        <section className="plant-asst-calc-panel">
          <div className="plant-asst-calc-panel__header">
            <FaLeaf aria-hidden="true" />
            <span>Seed selection</span>
          </div>
          <div className="plant-asst-calc-preset-list" role="radiogroup" aria-label="Seed preset">
            {seedOptions.length === 0 ? (
              <p className="plant-asst-calc-panel__empty">No seed presets available.</p>
            ) : (
              seedOptions.map((preset) => (
                <PresetPickerRow
                  key={preset.id}
                  preset={preset}
                  isSelected={selectedSeedId === preset.id}
                  acres={selectedTotal}
                  accentClass="is-seed"
                  onSelect={(id) => onSeedSelect(selectedSeedId === id ? '' : id)}
                />
              ))
            )}
          </div>
        </section>

        <section className="plant-asst-calc-panel">
          <div className="plant-asst-calc-panel__header">
            <FaTint aria-hidden="true" />
            <span>Fertilizer selection</span>
          </div>
          <div className="plant-asst-calc-preset-list" role="radiogroup" aria-label="Fertilizer preset">
            {fertilizerOptions.length === 0 ? (
              <p className="plant-asst-calc-panel__empty">No fertilizer presets available.</p>
            ) : (
              fertilizerOptions.map((preset) => (
                <PresetPickerRow
                  key={preset.id}
                  preset={preset}
                  isSelected={selectedFertilizerId === preset.id}
                  acres={selectedTotal}
                  accentClass="is-fert"
                  onSelect={(id) => onFertilizerSelect(selectedFertilizerId === id ? '' : id)}
                />
              ))
            )}
          </div>
        </section>
      </div>

      {showBreakdown && (
        <section className="plant-asst-calc-breakdown">
          <div className="plant-asst-calc-panel__header">
            <FaChartBar aria-hidden="true" />
            <span>Material breakdown</span>
            {!hasSavedGroup && (
              <span className="plant-asst-calc-breakdown__hint">Preview rates — save to apply adjustments</span>
            )}
          </div>
          <div className="plant-asst-calc-breakdown__body">
            <BreakdownRow item={breakdown.seed} kind="Seed" />
            <BreakdownRow item={breakdown.fertilizer} kind="Fertilizer" />
            <div className="plant-asst-calc-breakdown__total-row">
              <span>Total material</span>
              <span>{Math.round(breakdown.totalLbs)} lbs</span>
            </div>
          </div>
        </section>
      )}

      {recommendationError && (
        <p className="plant-asst-calc-error" role="alert">
          {recommendationError}
        </p>
      )}

      <div className="plant-asst-calc-actions">
        {isComposeMode && (
          <YButton
            variant="primary"
            size="sm"
            data-testid="plant-asst-save-estimate-btn"
            disabled={saveEstimateDisabled || saveEstimateLoading}
            onClick={onSaveEstimate}
          >
            {saveEstimateLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              <>
                <FaSave aria-hidden="true" />
                Save estimate
              </>
            )}
          </YButton>
        )}
        {isViewMode && (
          <YButton
            variant="secondary"
            size="sm"
            data-testid="plant-asst-edit-group-btn"
            onClick={onEditGroup}
          >
            <FaEdit aria-hidden="true" />
            Edit group
          </YButton>
        )}
        {canExportPdf && (
          <YButton
            variant="secondary"
            size="sm"
            data-testid="plant-asst-export-pdf-btn"
            disabled={exportPdfLoading}
            onClick={onExportPdf}
          >
            {exportPdfLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Preparing PDF…
              </>
            ) : (
              <>
                <FaFileDownload aria-hidden="true" />
                Download PDF
              </>
            )}
          </YButton>
        )}
        <YButton variant="ghost" size="sm" onClick={onGoToPlots}>
          My Plots
        </YButton>
      </div>
    </div>
  );
}
